import React, { useEffect, useMemo, useState } from "react";
import "./ComponentList.scss";
import { useDispatch } from "react-redux";
import SideNavbar from "../../components/Navbar/SideNavbar";
import { useNavigate, useParams } from "react-router-dom";
import { copyComponent, getComponents, getLayout } from "../../../core/actionCreators/lmsActionCreator";
import { useSelector } from "react-redux";
import ComponentHeader from "../../components/Header/ComponentHeader";
import Loader from "../../components/Loader/Loader";
import toast from "react-hot-toast";
import { formatTimestamp } from "../../../utils/helper";

const ComponentList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [layoutId, setLayoutId] = useState("")
  let { layout, components, isLoading } = useSelector((s: any) => s.lmsStore);
  const { user } = useSelector((s: any) => s.authStore);

  const [searchTerm, setSearchTerm] = useState("")
  //Keyed by component id so a selection survives a new search
  const [selectedComponents, setSelectedComponents] = useState<any[]>([])

  const componentList = useMemo(() => components?.data || [], [components])
  const selectedIds = useMemo(() => selectedComponents.map((c) => c.id), [selectedComponents])

  const isSelected = (component: any) => selectedIds.includes(component.id)

  const toggleComponentHandler = (component: any) => {
    if (isSelected(component)) {
      setSelectedComponents(selectedComponents.filter((c) => c.id !== component.id))
      return
    }
    setSelectedComponents([...selectedComponents, component])
  }

  //Only the components currently on screen, a search narrows what "all" means
  const areAllVisibleSelected = componentList.length > 0 && componentList.every((c: any) => selectedIds.includes(c.id))

  const toggleAllVisibleHandler = () => {
    if (areAllVisibleSelected) {
      const visibleIds = componentList.map((c: any) => c.id)
      setSelectedComponents(selectedComponents.filter((c) => !visibleIds.includes(c.id)))
      return
    }
    const missing = componentList.filter((c: any) => !selectedIds.includes(c.id))
    setSelectedComponents([...selectedComponents, ...missing])
  }

  //Every component name is suffixed with the layout code it belongs to, which reads
  //better as its own chip. These come from every layout, so the code cannot be assumed
  //to be the one in the header
  const splitComponentName = (componentName: string) => {
    const fullName = componentName || ""
    const separator = fullName.lastIndexOf(" - ")
    if (separator === -1) return { name: fullName, code: "" }

    const code = fullName.slice(separator + 3)
    //A name that merely contains a dash is left intact, only a trailing code is split off
    if (!code || /\s/.test(code)) return { name: fullName, code: "" }
    return { name: fullName.slice(0, separator), code }
  }

  const statusLabel = (component: any) => {
    const status = component?.status
    if (status === undefined || status === null) return null
    const isActive = typeof status === "string" ? status.toLowerCase() === "active" : Boolean(status)
    return isActive ? "Active" : "Inactive"
  }

  const addComponentHandler = () => {
    if (selectedComponents.length == 0) {
      toast.error("Please select at least one component.")
      return
    }
    const componentIds = selectedComponents.map((d) => {
      return {
        "componentId": d.id
      }
    })

    let requestPayload = {
      "layoutId": layoutId,
      "componentIds": componentIds,
      "modifiedBy": user.id || -1
    }

    let callback = (r: any) => {
      if (!r) {
        toast.success(`${selectedComponents.length} component${selectedComponents.length > 1 ? "s" : ""} copied.`)
        navigate(`/layout/${layoutId}`)
        return
      }
      toast.error(r?.statusDescription || "Could not copy the selected components.")
    }
    dispatch(copyComponent(requestPayload, callback))
  }


  useEffect(() => {
    if (params.layoutId) {
      setLayoutId(params.layoutId)
      dispatch(getLayout(params.layoutId, null));
    }
  }, [params]);

  //Debounced so typing does not fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getComponents(`componentName=${searchTerm}`, null))
    }, 400)
    return () => clearTimeout(timer)
  }, [searchTerm]);

  return (
    <>
      <div className="flex mainWrapper w-full">
        <div className="sidenav-wrapper">
          <SideNavbar />
        </div>
        <div className="right-container">
          <div className="header-container">
            <ComponentHeader layoutId={layoutId}
              layoutName={layout?.cmsLayoutResponse?.layoutName}
              device={layout?.cmsLayoutResponse?.device}
              layoutCode={layout?.cmsLayoutResponse?.layoutCode}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          </div>

          <div className="search-component body-container p-[15px] pb-[90px] bg-sky-50">
            <div className="search-component-inner">

              <div className="section-card">
                <div className="results-bar flex items-center justify-between">
                  <div>
                    <p className="section-title mb-0">
                      {searchTerm ? <>Results for “{searchTerm}”</> : <>All components</>}
                    </p>
                    <p className="section-subtitle">
                      {isLoading ? "Loading components…"
                        : `${componentList.length} component${componentList.length === 1 ? "" : "s"} available to copy into this page`}
                    </p>
                  </div>

                  {componentList.length ?
                    <button onClick={toggleAllVisibleHandler} className="link-action">
                      {areAllVisibleSelected ? "Clear selection" : "Select all"}
                    </button>
                    : null}
                </div>

                {isLoading ? <Loader /> : <>
                  {componentList.length ?
                    <ul className="component-list">
                      {componentList.map((l: any, index: number) => {
                        const selected = isSelected(l)
                        const status = statusLabel(l)
                        const { name, code } = splitComponentName(l.componentName)
                        return (
                          <li key={l.id || index} className={`component-row ${selected ? "is-selected" : ""}`}>
                            <label className="flex items-start w-full cursor-pointer">
                              <input
                                checked={selected}
                                onChange={() => toggleComponentHandler(l)}
                                className="mr-[12px] mt-[3px]"
                                type="checkbox" />
                              <div className="component-info">
                                <h4 title={l.componentName}>{name}</h4>
                                <div className="component-meta flex items-center flex-wrap">
                                  {code ? <span title="Layout code" className="meta-chip is-code">{code}</span> : null}
                                  {l.id ? <span className="meta-chip">ID {l.id}</span> : null}
                                  {l.componentType ? <span className="meta-chip">{l.componentType}</span> : null}
                                  {status ? <span className={`status-chip ${status === "Active" ? "is-active" : "is-inactive"}`}>{status}</span> : null}
                                  {l.updatedAt ? <span className="meta-date">Updated {formatTimestamp(l.updatedAt)}</span> : null}
                                </div>
                              </div>
                            </label>
                          </li>
                        )
                      })}
                    </ul>
                    :
                    <div className="empty-components p-[30px] text-center">
                      <p className="text-sm text-[#43425D] font-semibold">
                        {searchTerm ? `No component matches “${searchTerm}”.` : "No components available."}
                      </p>
                      <p className="text-xs text-[#959595] mt-[5px]">
                        {searchTerm ? "Try a shorter keyword or clear the search." : "Create a component from the page first, then copy it here."}
                      </p>
                    </div>}
                </>}
              </div>

              <div className="btm-fixed-strip">
                <div className="btm-fixed-strip-inner bg-white flex justify-between items-center p-[15px]">
                  <div className="flex items-center">
                    <p>{selectedComponents.length} component{selectedComponents.length === 1 ? "" : "s"} selected</p>
                    {selectedComponents.length ?
                      <button onClick={() => setSelectedComponents([])} className="link-action ml-[15px]">Clear</button>
                      : null}
                  </div>
                  <button
                    onClick={addComponentHandler}
                    disabled={!selectedComponents.length || isLoading}
                    className="py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed">
                    Select and Copy Component
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComponentList;
